import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Smartphone,
  Key,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { apiService } from '@/services/api';
import { PhoneNumber, ActivatePhoneResponse } from '@/types/meta';

interface MetaActivationWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  { id: 1, title: 'Credenciais', description: 'Informe WABA ID e Access Token' },
  { id: 2, title: 'Seleção de Phone', description: 'Escolha o número de telefone' },
  { id: 3, title: 'Ativação', description: 'Confirme e ative a API Meta' },
];

const MetaActivationWizard: React.FC<MetaActivationWizardProps> = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Etapa 1: Credenciais
  const [wabaId, setWabaId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  // Etapa 2: Phone Numbers
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState<string | null>(null);

  // Etapa 3: Resultado
  const [activationResult, setActivationResult] = useState<{
    registered: boolean;
    subscribed: boolean;
  } | null>(null);

  const handleClose = () => {
    // Resetar estados ao fechar
    setCurrentStep(1);
    setWabaId('');
    setAccessToken('');
    setPhoneNumbers([]);
    setSelectedPhoneId(null);
    setActivationResult(null);
    setError(null);
    onClose();
  };

  const handleStep1Next = async () => {
    if (!wabaId.trim() || !accessToken.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post<ActivatePhoneResponse>('/meta-api/activate', {
        waba_id: wabaId,
        access_token: accessToken,
      });

      if (response.success && response.data?.phoneNumbers) {
        setPhoneNumbers(response.data.phoneNumbers);
        setCurrentStep(2);
        toast({
          title: 'Sucesso',
          description: `${response.data.phoneNumbers.length} número(s) encontrado(s)`,
        });
      } else {
        throw new Error(response.error || 'Erro ao buscar phone numbers');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao buscar phone numbers';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Next = () => {
    if (!selectedPhoneId) {
      setError('Por favor, selecione um número de telefone');
      return;
    }
    setError(null);
    setCurrentStep(3);
  };

  const handleStep3Activate = async () => {
    if (!selectedPhoneId) {
      setError('Phone ID não selecionado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post<ActivatePhoneResponse>('/meta-api/activate', {
        waba_id: wabaId,
        access_token: accessToken,
        phone_id: selectedPhoneId,
      });

      if (response.success && response.data) {
        setActivationResult({
          registered: response.data.registeredPhone?.success || false,
          subscribed: response.data.subscribed?.success || false,
        });
        toast({
          title: 'Sucesso',
          description: 'API Meta ativada com sucesso!',
        });
      } else {
        throw new Error(response.error || 'Erro ao ativar API Meta');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao ativar API Meta';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getQualityRatingColor = (rating: string) => {
    switch (rating) {
      case 'GREEN':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'YELLOW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'RED':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getVerificationStatusColor = (status: string) => {
    if (status === 'VERIFIED') {
      return 'bg-green-100 text-green-800 border-green-300';
    } else if (status === 'EXPIRED') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="waba-id">WABA ID *</Label>
              <Input
                id="waba-id"
                type="text"
                placeholder="Ex: 24584548967799600"
                value={wabaId}
                onChange={(e) => setWabaId(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-gray-500">
                ID da sua WhatsApp Business Account
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="access-token">Access Token (Bearer Token) *</Label>
              <Input
                id="access-token"
                type="password"
                placeholder="Cole seu token de acesso aqui"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-gray-500">
                Token de acesso da API Meta (permanente)
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {phoneNumbers.length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Nenhum número de telefone encontrado. Verifique suas credenciais.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Selecione o número de telefone que deseja ativar:
                </p>
                <div className="grid gap-3 max-h-96 overflow-y-auto">
                  {phoneNumbers.map((phone) => (
                    <Card
                      key={phone.id}
                      className={`cursor-pointer transition-all ${
                        selectedPhoneId === phone.id
                          ? 'border-primary-500 border-2 bg-primary-50'
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedPhoneId(phone.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-900">
                                {phone.verified_name}
                              </h4>
                              {selectedPhoneId === phone.id && (
                                <CheckCircle className="h-5 w-5 text-primary-600" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              {phone.display_phone_number}
                            </p>
                            <p className="text-xs text-gray-500 font-mono mb-2">
                              ID: {phone.id}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Badge
                                variant="outline"
                                className={getQualityRatingColor(phone.quality_rating)}
                              >
                                {phone.quality_rating}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={getVerificationStatusColor(phone.code_verification_status)}
                              >
                                {phone.code_verification_status}
                              </Badge>
                              {phone.platform_type && (
                                <Badge variant="outline">
                                  {phone.platform_type}
                                </Badge>
                              )}
                            </div>
                            {phone.webhook_configuration?.application && (
                              <p className="text-xs text-gray-500 mt-2">
                                Webhook: {phone.webhook_configuration.application}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-gray-900 mb-3">Resumo da Ativação</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">WABA ID:</span>
                  <span className="font-mono text-gray-900">{wabaId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Número Selecionado:</span>
                  <span className="text-gray-900">
                    {phoneNumbers.find(p => p.id === selectedPhoneId)?.display_phone_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Nome Verificado:</span>
                  <span className="text-gray-900">
                    {phoneNumbers.find(p => p.id === selectedPhoneId)?.verified_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone ID:</span>
                  <span className="font-mono text-gray-900">
                    {selectedPhoneId}
                  </span>
                </div>
              </div>
            </div>

            {activationResult ? (
              <div className="space-y-3">
                <Alert className={activationResult.registered && activationResult.subscribed ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}>
                  {activationResult.registered && activationResult.subscribed ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        <strong>Ativação concluída com sucesso!</strong>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          <li>Phone number registrado</li>
                          <li>Aplicação subscrita ao WABA</li>
                        </ul>
                      </AlertDescription>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <AlertDescription className="text-yellow-800">
                        Ativação parcial. Verifique os resultados acima.
                      </AlertDescription>
                    </>
                  )}
                </Alert>
              </div>
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Ao clicar em "Ativar API Meta", o sistema irá:
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Registrar o phone number com PIN padrão</li>
                    <li>Subscrever a aplicação ao WABA</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary-600" />
            Ativar API Meta
          </DialogTitle>
          <DialogDescription>
            Configure e ative sua integração com a API Meta em 3 etapas simples
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} style={{ display: 'contents' }}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      index + 1 === currentStep
                        ? 'bg-primary-600 text-white'
                        : index + 1 < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <div className={`mt-2 text-xs text-center max-w-[100px] ${index + 1 === currentStep ? 'font-semibold text-primary-600' : ''}`}>
                    {step.title}
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 transition-colors ${
                      index + 1 < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleClose : () => setCurrentStep(currentStep - 1)}
            disabled={loading}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? 'Cancelar' : 'Anterior'}
          </Button>

          <div className="flex gap-2">
            {currentStep === 1 && (
              <Button
                onClick={handleStep1Next}
                disabled={loading || !wabaId.trim() || !accessToken.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    Buscar Phone Numbers
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}

            {currentStep === 2 && (
              <Button
                onClick={handleStep2Next}
                disabled={!selectedPhoneId}
              >
                Continuar
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}

            {currentStep === 3 && (
              <Button
                onClick={handleStep3Activate}
                disabled={loading || activationResult !== null}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Ativando...
                  </>
                ) : activationResult ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Concluído
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Ativar API Meta
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MetaActivationWizard;
